import React, { useEffect, useMemo, useRef, useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { reduxForm, getFormValues, Field } from 'redux-form';

import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';

import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';

import {
    HTMLinput,
    TextInput,
    Modal,
    HTMLField,
    EmojiInput,
} from '../../_Components';

const htmlToText = (html) => {
    if (!html) return '';
    return String(html)
        .replace(/<style[\s\S]*?<\/style>/gi, ' ')
        .replace(/<script[\s\S]*?<\/script>/gi, ' ')
        .replace(/<[^>]+>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/\s+/g, ' ')
        .trim();
};

const highlight = (text, query) => {
    if (!query || !text) return text || '';
    const i = String(text).toLowerCase().indexOf(query);
    if (i < 0) return text;
    return (
        <>
            {text.slice(0, i)}
            <mark>{text.slice(i, i + query.length)}</mark>
            {text.slice(i + query.length)}
        </>
    );
};

const snippet = (text, query) => {
    if (!query || !text) return '';
    const i = text.toLowerCase().indexOf(query);
    if (i < 0) return '';
    const from = Math.max(0, i - 24);
    const chunk = text.slice(from, from + 70);
    return `${from > 0 ? '…' : ''}${chunk}${text.length > from + 70 ? '…' : ''}`;
};

const notificationIcon = (type) => {
    switch (type) {
        case 'error': return '🚨';
        case 'warning': return '⚠️';
        case 'success': return '✅';
        default: return 'ℹ️';
    }
};

const TypeInput = ({ input: { value, onChange } }) => (
    <div style={{ margin: '0 16px 0 0', display: 'inline-flex' }}>
        {['success', 'info', 'warning', 'error'].map((type) => (
            <Chip
                key={type}
                size="small"
                color={type === 'error' ? 'error' : type}
                variant={value === type ? 'filled' : 'outlined'}
                label={notificationIcon(type)}
                onClick={() => onChange(type)}
                style={{ fontSize: '0.6rem', marginRight: 4, cursor: 'pointer' }}
            />
        ))}
    </div>
);

const ArticleForm = () => (
    <div>
        <Field name="name" component={TextInput} label="Заголовок статьи" />
        <Field name="text" component={HTMLinput} label="Содержание статьи" />
    </div>
);

const SectionForm = () => (
    <div style={{ display: 'flex', gap: 8 }}>
        <Field name="name" component={TextInput} label="Название раздела" />
        <Field name="icon" component={EmojiInput} label="Иконка раздела" />
    </div>
);

const NotificationForm = () => (
    <div>
        <Field name="name" component={TextInput} label="Название" />
        <Field name="type" component={TypeInput} label="Тип" />
        <Field name="body" component={HTMLinput} />
    </div>
);

const KnowledgeBase = ({ formValues, change, save, mode = 'read' }) => {
    const editing = mode === 'edit';
    const [selectedSection, setSelectedSection] = useState('main');
    const [selectedArticle, setSelectedArticle] = useState(0);
    const [query, setQuery] = useState('');
    const [editArticleDialog, setEditArticleDialog] = useState(false);
    const [editingArticle, setEditingArticle] = useState(null);
    const [creatingArticle, setCreatingArticle] = useState(false);
    const [newLink, setNewLink] = useState(null);
    const [editingSection, setEditingSection] = useState(null);
    const [editSectionDialog, setEditSectionDialog] = useState(false);
    const [editNotificationDialog, setEditNotificationDialog] = useState(false);
    const [editingNotification, setEditingNotification] = useState(null);
    const [creatingNotification, setCreatingNotification] = useState(false);
    const [shouldSave, setShouldSave] = useState(false);
    const bodyRef = useRef(null);

    useEffect(() => {
        if (shouldSave && save && typeof save === 'function') {
            save();
            setShouldSave(false);
        }
    }, [formValues, shouldSave, save]);

    useEffect(() => {
        if (bodyRef.current) bodyRef.current.scrollTop = 0;
    }, [selectedSection, selectedArticle]);

    const q = query.trim().toLowerCase();
    const pages = (formValues && formValues.pages) || [];
    const categories = (formValues && formValues.categories) || [];
    const notifications = (formValues && formValues.notifications) || [];

    const sections = useMemo(() => {
        const list = [
            { key: 'main', name: 'База знаний', icon: '📋', items: pages, kind: 'articles' },
            ...categories.map((category, index) => ({
                key: String(index),
                name: category.name,
                icon: category.icon,
                items: category.articles || [],
                kind: 'articles',
            })),
            { key: 'notifications', name: 'Уведомления', icon: '🔔', items: notifications, kind: 'notifications' },
        ];
        return list;
    }, [pages, categories, notifications]);

    const currentSection = sections.find((section) => section.key === selectedSection) || sections[0];

    const articleText = (item, kind) => {
        const name = (item && item.name) || '';
        const body = kind === 'notifications' ? htmlToText(item && item.body) : htmlToText(item && item.text);
        return `${name} ${body}`.toLowerCase();
    };

    const matchesQuery = (item, kind) => {
        if (!q) return true;
        return articleText(item, kind).includes(q);
    };

    const visibleItems = !currentSection
        ? []
        : (currentSection.items || [])
            .map((item, index) => ({ item, index }))
            .filter(({ item }) => matchesQuery(item, currentSection.kind));

    const allArticles = useMemo(() => {
        const list = [];
        pages.forEach((item, index) => {
            if (item && item.name) list.push({ item, section: 'main', index, kind: 'articles' });
        });
        categories.forEach((category, si) => {
            (category.articles || []).forEach((item, index) => {
                if (item && item.name) {
                    list.push({ item, section: String(si), index, kind: 'articles' });
                }
            });
        });
        return list;
    }, [pages, categories]);

    const findById = (id) => {
        if (id == null) return null;
        const sid = String(id);
        const found = allArticles.find(({ item }) => item._id != null && String(item._id) === sid);
        return found || null;
    };

    const findByName = (name) => {
        if (!name) return null;
        return allArticles.find(({ item }) => item.name === name) || null;
    };

    const currentItem = currentSection && selectedArticle != null
        ? currentSection.items[selectedArticle]
        : null;

    const goTo = (sectionKey, articleIndex, clearSearch) => {
        setSelectedSection(sectionKey);
        setSelectedArticle(articleIndex);
        if (clearSearch) {
            setQuery('');
        }
    };

    const handleSectionSelect = (sectionKey) => {
        const next = sections.find((section) => section.key === sectionKey);
        setSelectedSection(sectionKey);
        if (!next || !next.items || !next.items.length) {
            setSelectedArticle(null);
            return;
        }
        const firstHit = next.items.findIndex((item) => matchesQuery(item, next.kind));
        setSelectedArticle(firstHit >= 0 ? firstHit : 0);
    };

    const openNewSection = () => {
        setEditingSection(null);
        setEditSectionDialog(true);
    };

    const handleEditSection = (sectionKey) => {
        if (sectionKey === 'main' || sectionKey === 'notifications') return;
        const initialSection = categories[Number(sectionKey)];
        setEditingSection({ ...initialSection, _key: sectionKey });
        setEditSectionDialog(true);
    };

    const handleSaveSection = (newData) => {
        const next = categories.slice();
        if (editingSection && editingSection._key != null) {
            const idx = Number(editingSection._key);
            next[idx] = { ...next[idx], name: newData.name, icon: newData.icon };
            change('categories', next);
        } else {
            next.push({ ...newData, articles: [] });
            change('categories', next);
            setSelectedSection(String(next.length - 1));
            setSelectedArticle(null);
        }
        setShouldSave(true);
        setEditingSection(null);
        setEditSectionDialog(false);
    };

    const deleteSection = (sectionKey) => {
        if (sectionKey === 'main' || sectionKey === 'notifications') return;
        const next = categories.slice();
        next.splice(Number(sectionKey), 1);
        change('categories', next);
        setShouldSave(true);
        handleSectionSelect('main');
    };

    const openNewArticle = () => {
        if (selectedSection === 'notifications') {
            setCreatingNotification(true);
            setEditingNotification({ name: '', type: 'info', body: '' });
            setEditNotificationDialog(true);
            return;
        }
        setCreatingArticle(true);
        setEditingArticle({ name: '', text: '', replies: [] });
        setEditArticleDialog(true);
    };

    const handleEditArticle = (article) => {
        if (selectedSection === 'notifications') {
            setCreatingNotification(false);
            setEditingNotification({ ...article });
            setEditNotificationDialog(true);
            return;
        }
        setCreatingArticle(false);
        setEditingArticle({ ...article });
        setEditArticleDialog(true);
    };

    const handleSaveArticle = (newData) => {
        if (creatingArticle || selectedArticle === null) {
            if (selectedSection === 'main') {
                const next = pages.slice();
                next.push(newData);
                change('pages', next);
                setSelectedArticle(next.length - 1);
            } else {
                const next = categories.slice();
                const articles = [...(next[Number(selectedSection)].articles || []), newData];
                next[Number(selectedSection)] = { ...next[Number(selectedSection)], articles };
                change('categories', next);
                setSelectedArticle(articles.length - 1);
            }
        } else if (selectedSection === 'main') {
            change(`pages[${selectedArticle}]`, {
                ...pages[selectedArticle],
                ...newData,
            });
        } else {
            change(`categories[${selectedSection}].articles[${selectedArticle}]`, {
                ...categories[Number(selectedSection)].articles[selectedArticle],
                ...newData,
            });
        }
        setShouldSave(true);
        setCreatingArticle(false);
        setEditingArticle(null);
        setEditArticleDialog(false);
    };

    const deleteArticle = () => {
        if (selectedArticle === null) return;
        if (selectedSection === 'notifications') {
            const next = notifications.slice();
            next.splice(selectedArticle, 1);
            change('notifications', next);
            setSelectedArticle(next.length ? Math.min(selectedArticle, next.length - 1) : null);
            setShouldSave(true);
            return;
        }
        if (selectedSection === 'main') {
            const next = pages.slice();
            next.splice(selectedArticle, 1);
            change('pages', next);
            setSelectedArticle(next.length ? Math.min(selectedArticle, next.length - 1) : null);
        } else {
            const next = categories.slice();
            const articles = [...(next[Number(selectedSection)].articles || [])];
            articles.splice(selectedArticle, 1);
            next[Number(selectedSection)] = { ...next[Number(selectedSection)], articles };
            change('categories', next);
            setSelectedArticle(articles.length ? Math.min(selectedArticle, articles.length - 1) : null);
        }
        setShouldSave(true);
    };

    const handleSaveNotification = (newData) => {
        const next = notifications.slice();
        if (creatingNotification || selectedArticle === null) {
            next.push(newData);
            change('notifications', next);
            setSelectedArticle(next.length - 1);
        } else {
            next[selectedArticle] = newData;
            change('notifications', next);
        }
        setShouldSave(true);
        setCreatingNotification(false);
        setEditingNotification(null);
        setEditNotificationDialog(false);
    };

    const handleMakePrimary = () => {
        if (selectedArticle === null || selectedArticle === 0) return;
        if (selectedSection === 'main') {
            const next = pages.slice();
            const [article] = next.splice(selectedArticle, 1);
            next.unshift(article);
            change('pages', next);
            setSelectedArticle(0);
        } else if (selectedSection !== 'notifications') {
            const next = categories.slice();
            const articles = [...(next[Number(selectedSection)].articles || [])];
            const [article] = articles.splice(selectedArticle, 1);
            articles.unshift(article);
            next[Number(selectedSection)] = { ...next[Number(selectedSection)], articles };
            change('categories', next);
            setSelectedArticle(0);
        }
        setShouldSave(true);
    };

    const handleAddLink = () => {
        if (!newLink || selectedArticle === null || selectedSection === 'notifications') return;
        const replies = (currentItem && currentItem.replies) || [];
        const updated = [...replies, {
            title: newLink.name,
            link: newLink._id,
        }];
        if (selectedSection === 'main') {
            change(`pages[${selectedArticle}].replies`, updated);
        } else {
            change(`categories[${selectedSection}].articles[${selectedArticle}].replies`, updated);
        }
        setNewLink(null);
        setShouldSave(true);
    };

    const removeLink = (key) => {
        const replies = ((currentItem && currentItem.replies) || []).filter((_, i) => i !== key);
        if (selectedSection === 'main') {
            change(`pages[${selectedArticle}].replies`, replies);
        } else {
            change(`categories[${selectedSection}].articles[${selectedArticle}].replies`, replies);
        }
        setShouldSave(true);
    };

    const handleLinkSelect = (reply) => {
        const found = findById(reply.link || reply.pages_id) || findByName(reply.title);
        if (found) goTo(found.section, found.index, true);
    };

    const linkTargets = allArticles
        .map(({ item }) => item)
        .filter((page) => page && page.name && (!currentItem || page._id !== currentItem._id));

    const articleCountLabel = q
        ? `${visibleItems.length} совпадений в разделе`
        : `${(currentSection && currentSection.items && currentSection.items.length) || 0} статей`;

    const renderArticleBody = () => {
        if (!currentSection) return null;

        if (!currentItem) {
            return (
                <div className="pc-kb-empty">
                    <b>Выберите статью</b>
                    Выберите раздел и статью слева, чтобы открыть текст.
                </div>
            );
        }

        if (currentSection.kind === 'notifications') {
            return (
                <div className="pc-kb-doc">
                    <div className="pc-kb-html">
                        {currentItem.body
                            ? <HTMLField value={currentItem.body} style={{ textIndent: 0 }} />
                            : 'Содержимое отсутствует'}
                    </div>
                </div>
            );
        }

        const replies = currentItem.replies || [];
        const usedIds = new Set(replies.map((reply) => reply && (reply.link || reply.pages_id) && String(reply.link || reply.pages_id)));

        return (
            <>
                <div className="pc-kb-doc">
                    <div className="pc-kb-html">
                        {currentItem.text
                            ? <HTMLField value={currentItem.text} style={{ textIndent: 0 }} />
                            : 'Содержимое отсутствует'}
                    </div>
                </div>
                <div className="pc-kb-next">
                    <div className="pc-kb-next__t">Куда дальше · {replies.length}</div>
                    {replies.length
                        ? replies.map((reply, index) => {
                            const found = findById(reply.link || reply.pages_id) || findByName(reply.title);
                            return (
                                <button
                                    key={`${reply.link || reply.title}-${index}`}
                                    type="button"
                                    className={`pc-kb-nx${found ? '' : ' is-broken'}`}
                                    title={found ? undefined : 'Статьи с такой ссылкой нет'}
                                    onClick={() => handleLinkSelect(reply)}
                                >
                                    <span className="pc-kb-nx__t">{reply.title || 'Без названия'}</span>
                                    {!found && <span className="pc-kb-bad">нет статьи</span>}
                                    {editing && (
                                        <span
                                            className="pc-kb-nx__rm"
                                            role="button"
                                            tabIndex={0}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                removeLink(index);
                                            }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </span>
                                    )}
                                    <span className="pc-kb-nx__go"><ArrowForwardIcon sx={{ fontSize: 16 }} /></span>
                                </button>
                            );
                        })
                        : <div className="pc-kb-muted">Переходов нет — оператор вернётся к списку статей.</div>
                    }
                    {editing && (
                        <div className="pc-kb-addnext">
                            <Autocomplete
                                options={linkTargets.filter((page) => !usedIds.has(page._id && String(page._id)))}
                                getOptionLabel={(option) => option.name || '—'}
                                id="kb-add-next"
                                clearOnEscape
                                value={newLink}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Добавить переход" size="small" />
                                )}
                                onChange={(event, value) => setNewLink(value)}
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={handleAddLink}
                                disabled={!newLink}
                            >
                                Добавить переход
                            </Button>
                        </div>
                    )}
                </div>
            </>
        );
    };

    const metaLine = () => {
        if (!currentSection || !currentItem) return '';
        if (currentSection.kind === 'notifications') {
            return `${currentSection.name} · ${notificationIcon(currentItem.type)}`;
        }
        return currentSection.name;
    };

    return (
        <>
            <div className="pc-kb-stage">
                <div className="pc-kb-panel">
                    <div className="pc-kb-phd">
                        <h2>Разделы</h2>
                        <div className="pc-kb-phd__rt">
                            {editing && (
                                <button type="button" className="pc-kb-ico" title="Новый раздел" onClick={openNewSection}>
                                    <AddIcon fontSize="inherit" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="pc-kb-pbd">
                        {sections.map((section) => {
                            const hits = (section.items || []).filter((item) => matchesQuery(item, section.kind)).length;
                            const total = (section.items || []).length;
                            const custom = section.key !== 'main' && section.key !== 'notifications';
                            return (
                                <div
                                    key={section.key}
                                    role="button"
                                    tabIndex={0}
                                    className={`pc-kb-sec${selectedSection === section.key ? ' is-cur' : ''}`}
                                    onClick={() => handleSectionSelect(section.key)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            handleSectionSelect(section.key);
                                        }
                                    }}
                                >
                                    <span className="pc-kb-sec__t">
                                        {section.icon ? `${section.icon} ` : ''}{section.name || 'Без названия'}
                                    </span>
                                    {editing && custom && (
                                        <span className="pc-kb-sec__acts" onClick={(e) => e.stopPropagation()}>
                                            <button type="button" title="Переименовать" onClick={() => handleEditSection(section.key)}>
                                                <EditOutlinedIcon sx={{ fontSize: 14 }} />
                                            </button>
                                            <button type="button" className="is-danger" title="Удалить раздел" onClick={() => deleteSection(section.key)}>
                                                <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                                            </button>
                                        </span>
                                    )}
                                    <span className={`pc-kb-sec__n${q && hits ? ' is-hit' : ''}`}>
                                        {q ? hits : total}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="pc-kb-panel">
                    <div className="pc-kb-toolbar">
                        <div className="pc-kb-srch">
                            <SearchIcon />
                            <input
                                className="pc-kb-inp"
                                value={query}
                                placeholder="Поиск по всей базе"
                                onChange={(e) => setQuery(e.target.value)}
                            />
                        </div>
                        {editing && (
                            <button
                                type="button"
                                className="pc-kb-ico pc-kb-ico--pri"
                                title={selectedSection === 'notifications' ? 'Новое уведомление' : 'Новая статья'}
                                onClick={openNewArticle}
                            >
                                <AddIcon fontSize="inherit" />
                            </button>
                        )}
                    </div>
                    <div className="pc-kb-toolbar" style={{ borderTop: 0, paddingTop: 0 }}>
                        <span className="pc-kb-count">{articleCountLabel}</span>
                    </div>
                    <div className="pc-kb-pbd">
                        {visibleItems.length
                            ? visibleItems.map(({ item, index }) => {
                                const title = item.name || 'Без названия';
                                const bodyText = currentSection.kind === 'notifications'
                                    ? htmlToText(item.body)
                                    : htmlToText(item.text);
                                const showSnip = q && !title.toLowerCase().includes(q) && bodyText.toLowerCase().includes(q);
                                return (
                                    <button
                                        key={item._id || `${currentSection.key}-${index}`}
                                        type="button"
                                        className={`pc-kb-art${selectedArticle === index ? ' is-cur' : ''}`}
                                        onClick={() => setSelectedArticle(index)}
                                    >
                                        <span className="pc-kb-art__ix">{index + 1}</span>
                                        <span className="pc-kb-art__t">
                                            {highlight(title, q)}
                                            {showSnip && (
                                                <span className="pc-kb-art__snip">
                                                    {highlight(snippet(bodyText, q), q)}
                                                </span>
                                            )}
                                        </span>
                                    </button>
                                );
                            })
                            : (
                                <div className="pc-kb-empty">
                                    <b>{q ? 'Ничего не найдено' : 'В разделе нет статей'}</b>
                                    {q ? 'Попробуйте другой запрос или другой раздел.' : (editing ? 'Добавьте статью кнопкой плюс.' : '')}
                                </div>
                            )
                        }
                    </div>
                </div>

                <div className="pc-kb-panel">
                    <div className="pc-kb-ahd">
                        <div style={{ minWidth: 0 }}>
                            <h3>{currentItem ? (currentItem.name || 'Без названия') : '—'}</h3>
                            <div className="pc-kb-ahd__sub">{currentItem ? metaLine() : ''}</div>
                        </div>
                        {editing && currentItem && (
                            <div className="pc-kb-ahd__rt">
                                {selectedArticle !== 0 && currentSection.kind === 'articles' && (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<StarOutlineIcon />}
                                        onClick={handleMakePrimary}
                                    >
                                        Сделать основной
                                    </Button>
                                )}
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<EditOutlinedIcon />}
                                    onClick={() => handleEditArticle(currentItem)}
                                >
                                    Редактировать
                                </Button>
                                <Button
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                    startIcon={<DeleteOutlineIcon />}
                                    onClick={deleteArticle}
                                >
                                    {currentSection.kind === 'notifications' ? 'Удалить' : 'Удалить статью'}
                                </Button>
                            </div>
                        )}
                    </div>
                    <div className="pc-kb-body" ref={bodyRef}>
                        <div className="pc-kb-sheet">
                            {renderArticleBody()}
                        </div>
                    </div>
                </div>
            </div>

            {editArticleDialog && (
                <Modal
                    width="lg"
                    title={creatingArticle ? 'Добавить статью' : 'Редактировать статью'}
                    open={editArticleDialog}
                    handleSaveClick={handleSaveArticle}
                    handleCloseClick={() => {
                        setEditArticleDialog(false);
                        setCreatingArticle(false);
                        setEditingArticle(null);
                    }}
                    fields={<ArticleForm />}
                    initial={editingArticle}
                />
            )}

            {editSectionDialog && (
                <Modal
                    title={editingSection ? 'Редактировать раздел' : 'Добавить раздел'}
                    open={editSectionDialog}
                    handleSaveClick={handleSaveSection}
                    handleCloseClick={() => {
                        setEditSectionDialog(false);
                        setEditingSection(null);
                    }}
                    fields={<SectionForm />}
                    initial={editingSection}
                />
            )}

            {editNotificationDialog && (
                <Modal
                    width="lg"
                    title={creatingNotification ? 'Добавить уведомление' : 'Редактировать уведомление'}
                    open={editNotificationDialog}
                    handleSaveClick={handleSaveNotification}
                    handleCloseClick={() => {
                        setEditNotificationDialog(false);
                        setCreatingNotification(false);
                        setEditingNotification(null);
                    }}
                    fields={<NotificationForm />}
                    initial={editingNotification}
                />
            )}
        </>
    );
};

const mapStateToProps = (state) => {
    const formValues = getFormValues('script')(state);
    return { formValues };
};

const enhance = compose(
    connect(mapStateToProps),
    reduxForm({
        form: 'script',
    })
);

export default enhance(KnowledgeBase);
